import * as React from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';

import {
	ActionMode,
	Button,
	ButtonVariant,
	Color,
} from '@app/components/Button/Button';
import { ListItem } from '@app/objects/List';

import { BrandLogo } from '@app/components/BrandLogo/BrandLogo';
import { PackageType } from '@app/objects/CruisePackages';
import { PriceTag } from '@app/components/Cards/SearchListCard/CardComponents/Price';
import { CardContent } from '@app/components/Cards/SearchListCard/CardComponents/CardContent';
import { CoverType } from '@app/objects/Routes/Route';
import { Image } from '@app/components/Image';

import fallback from '@image/search-fallback.png';

import '@app/scss/components/searchListCard.scss';

export type SearchListCardProps = ListItem;
const BODY_LOCK_CLASS = 'no-scroll-body';
const SAIL_DATES_URL = 'https://apps.onlinevacationcenter.com/specialsSailDates.cfm';

function getGroupMasterId(links: Array<string | undefined>): string | null {
	const paramNames = ['gmId', 'groupmasterid', 'groupMasterId', 'groupmasterId'];

	const resolvedFromLink = links
		.filter(Boolean)
		.map((link) => {
			try {
				const parsedUrl = new URL(link as string, process.env.app.hosts.link);

				return paramNames
					.map((paramName) => parsedUrl.searchParams.get(paramName))
					.find(Boolean) || null;
			} catch (error) {
				// Ignore malformed links and continue trying other sources.
				return null;
			}
		})
		.find(Boolean);

	return resolvedFromLink || null;
}

export const SearchListCard: React.FC<SearchListCardProps> = (props: SearchListCardProps) => {
	const [isOpen, setIsOpen] = React.useState<boolean>(false);
	const [canCloseByOverlay, setCanCloseByOverlay] = React.useState<boolean>(false);
	const [iframeFailed, setIframeFailed] = React.useState<boolean>(false);
	const modalContainer = document.getElementById('modal-root') || document.body;
	const priceValue = props.price?.price ?? null;
	const groupMasterId = getGroupMasterId([props.detailsLink, props.bookLink]);
	const bookingParams = new URLSearchParams({
		gmId: groupMasterId || '',
		taObRef: '',
		aView: '0',
	});
	const bookingSrc = groupMasterId
		? `${SAIL_DATES_URL}?${bookingParams.toString()}`
		: `${process.env.app.hosts.link}${props.bookLink || props.detailsLink}`;

	const isJustifyCenter = !priceValue && props.detailsLink;
	const isNoTour = props.type !== PackageType.Tour;

	React.useEffect(() => {
		document.body.classList.toggle(BODY_LOCK_CLASS, isOpen);

		return () => {
			document.body.classList.remove(BODY_LOCK_CLASS);
		};
	}, [isOpen]);

	React.useEffect(() => {
		if (!isOpen) {
			setCanCloseByOverlay(false);
			setIframeFailed(false);

			return undefined;
		}

		const timeout = window.setTimeout(() => {
			setCanCloseByOverlay(true);
		}, 200);

		return () => {
			window.clearTimeout(timeout);
		};
	}, [isOpen]);

	return (
		<>
			<div className={clsx('search-list-card', !props.image && 'search-list-card_special-grid')}>
				{
					props.image
						? (
							<Image
								src={props.image}
								fallback={fallback}
								alt="Destination"
								className="search-list-card__left-block"
							/>
						)
						: null

				}
				<div className="search-list-card__header content-valign_left content-halign_center">
					<div className="col-10 col-sm-4">
						<img
							src={props.cruiseLineLogo}
							alt={`${props.cruiseLineName} logo`}
							className="search-list-card__header__logo"
						/>
					</div>
					<div className="col-10 col-sm-8 content-valign_center">
						<h4 className="font-family_primary condensed font-weight_bold text-size_medium">
							{props.title}
						</h4>
					</div>
				</div>
				<CardContent
					tour={props.type === PackageType.Tour}
					duration={props.duration}
					subtitle={props.subTitle}
					departureDates={props.dates}
					ports={props.coverType !== CoverType.Map ? props.ports : []}
					includes={props.inclusions}
				/>

				<div className="search-list-card__brand-logo-block">
					{
						isNoTour ? (
							<BrandLogo type={props.type} className="search-list-card__brand-logo" />
						) : null
					}
				</div>

				<div className={clsx('search-list-card__right-block content-halign_center', isJustifyCenter && 'content-valign_center')}>
					{props.price && <PriceTag price={priceValue} name={props.price?.description} tour={props.type === PackageType.Tour} />}
					<div className={clsx('search-list-card__btn-block', !isJustifyCenter && 'search-list-card__btn-block_auto')}>
						{props.detailsLink && (
							<div className="search-list-card__info-btn">
								<Button
									variant={ButtonVariant.Outlined}
									color={Color.Dark}
									className="search-list-card__more-btn"
									action={ActionMode.ExternalLink}
									link={`${process.env.app.hosts.link}${props.detailsLink}`}
								// action={ActionMode.InternalLink}
								// link={RoutePaths.moreDetailsPage}
								>
									More Details
								</Button>
							</div>
						)}
						{isNoTour && (
							<div
								className="search-list-card__action-btn"
								onMouseDown={(e) => e.stopPropagation()}
								onClick={(e) => {
									e.stopPropagation();
									setIsOpen(true);
								}}
							>
								<Button
									variant={ButtonVariant.Filled}
									style={{ width: '100%' }}
								>
									Book Now
								</Button>
							</div>
						)}
					</div>
				</div>
			</div>
			{isOpen && createPortal(
				<div
					onClick={() => {
						if (canCloseByOverlay) {
							setIsOpen(false);
						}
					}}
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						width: '100%',
						height: '100vh',
						background: 'rgba(9, 14, 44, 0.65)',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						zIndex: 2147483000,
					}}
				>
					<div
						className="search-list-card__inclusion-modal"
						onClick={(e) => e.stopPropagation()}
						style={{
							position: 'relative',
							minWidth: '350px',
							width: '75%',
							maxWidth: '850px',
							maxHeight: 'calc(100vh - 32px)',
							overflow: 'auto',
							padding: 12,
							background: '#fff',
							borderRadius: 5,
							boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)',
						}}
					>
						<button
							type="button"
							onClick={() => setIsOpen(false)}
							aria-label="Close booking modal"
							style={{
								position: 'absolute',
								top: -15,
								right: -15,
								width: 34,
								height: 34,
								borderRadius: '50%',
								border: '3px solid #fff',
								background: '#3f66e0',
								color: '#fff',
								cursor: 'pointer',
								fontSize: 18,
								lineHeight: '28px',
							}}
						>
							x
						</button>
						{iframeFailed && (
							<div
								style={{
									padding: '12px 8px',
									marginBottom: 8,
									background: '#fff4f4',
									color: '#8a1f1f',
									borderRadius: 4,
								}}
							>
								Booking page failed to load inside modal. You can continue in a new tab.
								<div style={{ marginTop: 8 }}>
									<a
										href={bookingSrc}
										target="_blank"
										rel="noreferrer"
										style={{
											padding: '8px 12px',
											background: '#3f66e0',
											color: '#fff',
											border: 'none',
											borderRadius: 4,
											textDecoration: 'none',
											display: 'inline-block',
										}}
									>
										Open booking in new tab
									</a>
								</div>
							</div>
						)}
						<iframe
							title={props.bookLink}
							height="100%"
							scrolling="yes"
							style={{
								width: '100%',
								border: 'none',
								minHeight: '560px',
								display: iframeFailed ? 'none' : 'block',
							}}
							onError={() => setIframeFailed(true)}
							src={bookingSrc}
						/>
					</div>
				</div>,
				modalContainer,
			)}
		</>
	);
};
